import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { ArrowLeft, Loader2, ChevronDown, ChevronUp, Plus, Trash2, Pencil } from 'lucide-react'
import {
  getMyHome, getMyProviders, getMyContracts, createMyContract, updateMyContract, deleteMyContract,
  getMyContractPricePoints, addMyContractPricePoint,
  type MyContract, type MyProvider, type PricingRule, type Fee, type TimeOfDayPeriod, type PriceTier,
} from '../api/energy-os'
import { Modal } from '../components/Modal'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'

// dateStart/dateEnd are plain 'YYYY-MM-DD' dates, not timestamps — displaying them via the
// business's full DATE_FORMAT (which includes a time-of-day) would be noisy, and re-parsing
// through `new Date()` risks a day shift depending on the viewer's timezone. Just show the
// digits the server sent.
function fmtDateOnly(d: string | null | undefined): string {
  if (!d) return '—'
  return d.length >= 10 ? d.slice(0, 10) : d
}

// Mirrors contract.service.ts's isContractEditable exactly: read-only once its own end date has
// passed, or once a later contract has already started — past pricing history doesn't change
// after the fact. This client-side check only disables the buttons for a better UX; the server
// enforces the same rule independently and is the real gate.
function isContractEditable(contract: MyContract, all: MyContract[], today = new Date()): boolean {
  const todayStr = today.toISOString().slice(0, 10)
  if (contract.dateEnd && contract.dateEnd < todayStr) return false
  const supersededBy = all.some((c) => c.id !== contract.id && c.dateStart > contract.dateStart && c.dateStart <= todayStr)
  return !supersededBy
}

function emptyRule(type: PricingRule['type']): PricingRule {
  switch (type) {
    case 'timeOfDay': return { type, monthlyFees: [], periods: [] }
    case 'incremental': return { type, monthlyFees: [], tiers: [] }
    case 'threshold': return { type, monthlyFees: [], tiers: [] }
    case 'dynamic': return { type, monthlyFees: [] }
  }
}

const RULE_TYPE_LABELS: Record<PricingRule['type'], string> = {
  timeOfDay: 'Time of day',
  incremental: 'Incremental (tiered, marginal)',
  threshold: 'Threshold (tiered, whole consumption)',
  dynamic: 'Dynamic (manual price log)',
}

export function EnergyOSHomeDetail() {
  const queryClient = useQueryClient()
  const [openContractId, setOpenContractId] = useState<string | null>(null)
  const didInitOpen = useRef(false)
  const [showAdd, setShowAdd] = useState(false)
  const [editingContract, setEditingContract] = useState<MyContract | null>(null)

  const { data: home } = useQuery({ queryKey: ['my-home'], queryFn: () => getMyHome().then((r) => r.data), retry: false })
  const { data: providers } = useQuery({
    queryKey: ['my-providers'],
    queryFn: () => getMyProviders().then((r) => r.data),
    staleTime: 5 * 60_000,
  })
  const { data: contracts, isLoading } = useQuery({
    queryKey: ['my-contracts'],
    queryFn: () => getMyContracts().then((r) => r.data),
  })
  const sorted = contracts ?? []

  // Expand the most recent contract by default (per the request), but only on first load — once
  // the user has toggled anything, respect their choice instead of re-forcing it open every time
  // this query refetches.
  useEffect(() => {
    if (!didInitOpen.current && sorted.length > 0) {
      setOpenContractId(sorted[0].id)
      didInitOpen.current = true
    }
  }, [sorted])

  const deleteContract = useMutation({
    mutationFn: (id: string) => deleteMyContract(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-contracts'] }),
  })

  function handleDelete(c: MyContract) {
    if (!window.confirm(`Delete the contract with ${c.retailerName}? This cannot be undone.`)) return
    deleteContract.mutate(c.id)
  }

  return (
    <div className="space-y-4">
      <Link to="/energy-os/dashboard" className="inline-flex items-center gap-1.5 text-xs text-ink-dim hover:text-ink transition-colors">
        <ArrowLeft size={13} /> Back to dashboard
      </Link>

      <section className="card p-5">
        <h1 className="text-base font-semibold text-ink">Home</h1>
        <p className="text-xs text-ink-dim mt-0.5">MPID {home?.mpid ?? '—'} · {home?.country ?? '—'}</p>
      </section>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">Contracts</h2>
        <Button variant="secondary" size="sm" onClick={() => setShowAdd(true)} icon={Plus}>
          Add contract
        </Button>
      </div>

      {isLoading ? (
        <div className="card p-5 flex items-center justify-center py-16">
          <Loader2 size={20} className="animate-spin text-ink-dim" />
        </div>
      ) : sorted.length === 0 ? (
        <section className="card p-5">
          <p className="text-xs text-ink-dim text-center py-10">
            No contracts yet — add one to start pricing this home's energy usage from a real retailer.
          </p>
        </section>
      ) : (
        sorted.map((c) => (
          <ContractAccordionItem
            key={c.id}
            contract={c}
            allContracts={sorted}
            isOpen={openContractId === c.id}
            onToggle={() => setOpenContractId(openContractId === c.id ? null : c.id)}
            onEdit={() => setEditingContract(c)}
            onDelete={() => handleDelete(c)}
          />
        ))
      )}

      {showAdd && (
        <ContractFormModal
          providers={providers ?? []}
          homeCountry={home?.country ?? null}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); queryClient.invalidateQueries({ queryKey: ['my-contracts'] }) }}
        />
      )}
      {editingContract && (
        <ContractFormModal
          providers={providers ?? []}
          homeCountry={home?.country ?? null}
          contract={editingContract}
          onClose={() => setEditingContract(null)}
          onSaved={() => { setEditingContract(null); queryClient.invalidateQueries({ queryKey: ['my-contracts'] }) }}
        />
      )}
    </div>
  )
}

function ContractAccordionItem({ contract, allContracts, isOpen, onToggle, onEdit, onDelete }: {
  contract: MyContract; allContracts: MyContract[]; isOpen: boolean; onToggle: () => void
  onEdit: () => void; onDelete: () => void
}) {
  const editable = isContractEditable(contract, allContracts)
  return (
    <section className="card p-0 overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-white/50 transition-colors">
        <div>
          <p className="text-sm font-semibold text-ink">{contract.retailerName}</p>
          <p className="text-xs text-ink-dim mt-0.5">
            {fmtDateOnly(contract.dateStart)} → {contract.dateEnd ? fmtDateOnly(contract.dateEnd) : 'ongoing'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!editable && <span className="inline-flex items-center gap-1.5 rounded-full bg-paper-sunken px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-ink-dim">Read-only</span>}
          {isOpen ? <ChevronUp size={16} className="text-ink-dim" /> : <ChevronDown size={16} className="text-ink-dim" />}
        </div>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 space-y-4 border-t border-white/60 pt-4">
          <div className="flex items-center gap-2">
            <Button
              variant="secondary" size="sm"
              onClick={onEdit} disabled={!editable}
              icon={Pencil}
            >
              Edit
            </Button>
            <button
              onClick={onDelete} disabled={!editable}
              className="flex items-center gap-1 text-xs text-red-500 border border-red-200 rounded-full px-2.5 py-1 bg-white hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
          <ChapterSummary title="Buying energy" rule={contract.buyPricingRule} contractId={contract.id} chapter="buy" />
          {contract.sellPricingRule && (
            <ChapterSummary title="Selling energy" rule={contract.sellPricingRule} contractId={contract.id} chapter="sell" />
          )}
        </div>
      )}
    </section>
  )
}

function ChapterSummary({ title, rule, contractId, chapter }: {
  title: string; rule: PricingRule; contractId: string; chapter: 'buy' | 'sell'
}) {
  return (
    <div>
      <p className="text-theme-xs font-semibold text-ink-dim uppercase tracking-wide mb-2">{title}</p>
      <div className="space-y-2">
        {rule.monthlyFees.length > 0 && (
          <div>
            <p className="text-xs text-ink-dim mb-1">Monthly fees</p>
            <ul className="space-y-0.5">
              {rule.monthlyFees.map((f, i) => (
                <li key={i} className="flex justify-between text-ink text-xs">
                  <span>{f.name}</span><span className="font-medium">{f.amount.toFixed(2)}€</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-xs text-ink-dim">{RULE_TYPE_LABELS[rule.type]}</p>
        {rule.type === 'timeOfDay' && (
          <ul className="space-y-0.5">
            {rule.periods.map((p, i) => (
              <li key={i} className="flex justify-between text-ink text-xs">
                <span>{p.name} ({p.startTime}–{p.endTime})</span><span className="font-medium">{p.pricePerKwh}€/kWh</span>
              </li>
            ))}
          </ul>
        )}
        {(rule.type === 'incremental' || rule.type === 'threshold') && (
          <ul className="space-y-0.5">
            {rule.tiers.map((t, i) => (
              <li key={i} className="flex justify-between text-ink text-xs">
                <span>{t.fromKwh} → {t.toKwh ?? '∞'} kWh</span><span className="font-medium">{t.pricePerKwh}€/kWh</span>
              </li>
            ))}
          </ul>
        )}
        {rule.type === 'dynamic' && <DynamicPricePointsSection contractId={contractId} chapter={chapter} />}
      </div>
    </div>
  )
}

function DynamicPricePointsSection({ contractId, chapter }: { contractId: string; chapter: 'buy' | 'sell' }) {
  const queryClient = useQueryClient()
  const [effectiveAt, setEffectiveAt] = useState('')
  const [price, setPrice] = useState('')
  const [error, setError] = useState('')

  const { data: points } = useQuery({
    queryKey: ['my-contract-price-points', contractId],
    queryFn: () => getMyContractPricePoints(contractId).then((r) => r.data),
  })
  const chapterPoints = (points ?? []).filter((p) => p.chapter === chapter).sort((a, b) => a.effectiveAt.localeCompare(b.effectiveAt))

  const addPoint = useMutation({
    mutationFn: () => addMyContractPricePoint(contractId, {
      chapter, effectiveAt: new Date(effectiveAt).toISOString(), pricePerKwh: parseFloat(price),
    }),
    onSuccess: () => {
      setError(''); setEffectiveAt(''); setPrice('')
      queryClient.invalidateQueries({ queryKey: ['my-contract-price-points', contractId] })
    },
    onError: (err: any) => setError(err.response?.data?.error || 'Failed to add price point'),
  })

  return (
    <div className="space-y-2">
      {chapterPoints.length === 0 ? (
        <p className="text-xs text-ink-dim">No price points logged yet.</p>
      ) : (
        <ul className="space-y-0.5">
          {chapterPoints.map((p) => (
            <li key={p.id} className="flex justify-between text-ink text-xs">
              <span>{p.effectiveAt.replace('T', ' ').slice(0, 16)}</span><span className="font-medium">{p.pricePerKwh}€/kWh</span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <input type="datetime-local" value={effectiveAt} onChange={(e) => setEffectiveAt(e.target.value)} className="field text-xs flex-1" />
        <input type="number" step="0.00001" placeholder="€/kWh" value={price} onChange={(e) => setPrice(e.target.value)} className="field text-xs w-24" />
        <Button
          variant="secondary" size="sm"
          onClick={() => addPoint.mutate()}
          disabled={addPoint.isPending || !effectiveAt || !price}
          className="whitespace-nowrap"
        >
          Add
        </Button>
      </div>
      {error && <p className="text-theme-xs text-red-500">{error}</p>}
    </div>
  )
}

function PricingRuleEditor({ label, value, onChange }: { label: string; value: PricingRule; onChange: (r: PricingRule) => void }) {
  function setFees(fees: Fee[]) {
    onChange({ ...value, monthlyFees: fees } as PricingRule)
  }
  function addFee() { setFees([...value.monthlyFees, { name: '', amount: 0 }]) }
  function updateFee(i: number, patch: Partial<Fee>) {
    setFees(value.monthlyFees.map((f, idx) => (idx === i ? { ...f, ...patch } : f)))
  }
  function removeFee(i: number) { setFees(value.monthlyFees.filter((_, idx) => idx !== i)) }

  function setPeriods(periods: TimeOfDayPeriod[]) {
    if (value.type === 'timeOfDay') onChange({ ...value, periods })
  }
  function setTiers(tiers: PriceTier[]) {
    if (value.type === 'incremental' || value.type === 'threshold') onChange({ ...value, tiers })
  }

  return (
    <div className="space-y-3 border border-paper-border rounded-lg p-3 bg-paper-sunken/50">
      <p className="text-xs font-semibold text-ink">{label}</p>
      <Field label="Pricing type">
        <select value={value.type} onChange={(e) => onChange(emptyRule(e.target.value as PricingRule['type']))} className="field">
          {(Object.keys(RULE_TYPE_LABELS) as PricingRule['type'][]).map((t) => (
            <option key={t} value={t}>{RULE_TYPE_LABELS[t]}</option>
          ))}
        </select>
      </Field>

      <div>
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-ink-dim">Monthly fees (all taxes included)</p>
          <button onClick={addFee} className="text-theme-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5"><Plus size={11} /> Add</button>
        </div>
        {value.monthlyFees.map((f, i) => (
          <div key={i} className="flex items-center gap-2 mb-1">
            <input value={f.name} onChange={(e) => updateFee(i, { name: e.target.value })} placeholder="Name (e.g. Abonnement)" className="field text-xs flex-1" />
            <input
              type="number" step="0.01" value={f.amount}
              onChange={(e) => updateFee(i, { amount: parseFloat(e.target.value) || 0 })}
              placeholder="€" className="field text-xs w-24"
            />
            <button onClick={() => removeFee(i)} className="text-ink-dim hover:text-red-500"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>

      {value.type === 'timeOfDay' && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-ink-dim">Periods (HH:mm, wraps past midnight if end &lt; start)</p>
            <button
              onClick={() => setPeriods([...value.periods, { name: '', startTime: '00:00', endTime: '23:59', pricePerKwh: 0 }])}
              className="text-theme-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
            >
              <Plus size={11} /> Add
            </button>
          </div>
          {value.periods.map((p, i) => (
            <div key={i} className="flex items-center gap-2 mb-1">
              <input
                value={p.name} placeholder="Name" className="field text-xs w-24"
                onChange={(e) => setPeriods(value.periods.map((x, idx) => (idx === i ? { ...x, name: e.target.value } : x)))}
              />
              <input
                type="time" value={p.startTime} className="field text-xs"
                onChange={(e) => setPeriods(value.periods.map((x, idx) => (idx === i ? { ...x, startTime: e.target.value } : x)))}
              />
              <input
                type="time" value={p.endTime} className="field text-xs"
                onChange={(e) => setPeriods(value.periods.map((x, idx) => (idx === i ? { ...x, endTime: e.target.value } : x)))}
              />
              <input
                type="number" step="0.00001" value={p.pricePerKwh} placeholder="€/kWh" className="field text-xs w-24"
                onChange={(e) => setPeriods(value.periods.map((x, idx) => (idx === i ? { ...x, pricePerKwh: parseFloat(e.target.value) || 0 } : x)))}
              />
              <button onClick={() => setPeriods(value.periods.filter((_, idx) => idx !== i))} className="text-ink-dim hover:text-red-500"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}

      {(value.type === 'incremental' || value.type === 'threshold') && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-ink-dim">
              Tiers ({value.type === 'incremental' ? 'each tier prices only the kWh within it' : 'whichever tier the month lands in prices the whole month'})
            </p>
            <button
              onClick={() => setTiers([...value.tiers, { fromKwh: 0, toKwh: null, pricePerKwh: 0 }])}
              className="text-theme-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
            >
              <Plus size={11} /> Add
            </button>
          </div>
          {value.tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-2 mb-1">
              <input
                type="number" value={t.fromKwh} placeholder="From kWh" className="field text-xs w-24"
                onChange={(e) => setTiers(value.tiers.map((x, idx) => (idx === i ? { ...x, fromKwh: parseFloat(e.target.value) || 0 } : x)))}
              />
              <input
                type="number" value={t.toKwh ?? ''} placeholder="To kWh (blank = ∞)" className="field text-xs w-28"
                onChange={(e) => setTiers(value.tiers.map((x, idx) => (idx === i ? { ...x, toKwh: e.target.value === '' ? null : parseFloat(e.target.value) } : x)))}
              />
              <input
                type="number" step="0.00001" value={t.pricePerKwh} placeholder="€/kWh" className="field text-xs w-24"
                onChange={(e) => setTiers(value.tiers.map((x, idx) => (idx === i ? { ...x, pricePerKwh: parseFloat(e.target.value) || 0 } : x)))}
              />
              <button onClick={() => setTiers(value.tiers.filter((_, idx) => idx !== i))} className="text-ink-dim hover:text-red-500"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      )}

      {value.type === 'dynamic' && (
        <p className="text-xs text-ink-dim">Price points are logged manually after the contract is created.</p>
      )}
    </div>
  )
}

function ContractFormModal({ providers, homeCountry, contract, onClose, onSaved }: {
  providers: MyProvider[]; homeCountry: string | null; contract?: MyContract; onClose: () => void; onSaved: () => void
}) {
  const filteredProviders = homeCountry ? providers.filter((p) => p.country === homeCountry) : providers
  const [retailerName, setRetailerName] = useState(contract?.retailerName ?? filteredProviders[0]?.name ?? '')
  const [dateStart, setDateStart] = useState(contract ? fmtDateOnly(contract.dateStart) : '')
  const [dateEnd, setDateEnd] = useState(contract?.dateEnd ? fmtDateOnly(contract.dateEnd) : '')
  const [buyRule, setBuyRule] = useState<PricingRule>(contract?.buyPricingRule ?? emptyRule('timeOfDay'))
  const [hasSell, setHasSell] = useState(!!contract?.sellPricingRule)
  const [sellRule, setSellRule] = useState<PricingRule>(contract?.sellPricingRule ?? emptyRule('timeOfDay'))
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)

  const isEdit = !!contract

  const handleSubmit = async () => {
    if (!retailerName) { setError('Select a retailer'); return }
    if (!dateStart) { setError('Start date is required'); return }
    setError(''); setPending(true)
    try {
      const payload = {
        dateStart, dateEnd: dateEnd || null, retailerName,
        buyPricingRule: buyRule, sellPricingRule: hasSell ? sellRule : null,
      }
      if (isEdit) await updateMyContract(contract!.id, payload)
      else await createMyContract(payload)
      onSaved()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save contract')
    } finally {
      setPending(false)
    }
  }

  return (
    <Modal title={isEdit ? 'Edit contract' : 'Add contract'} onClose={onClose} size="lg">
      <div className="space-y-4">
        <Field label="Retailer">
          <select value={retailerName} onChange={(e) => setRetailerName(e.target.value)} className="field">
            <option value="">Select…</option>
            {filteredProviders.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date">
            <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} className="field" />
          </Field>
          <Field label="End date (optional)">
            <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} className="field" />
          </Field>
        </div>

        <PricingRuleEditor label="Buying energy" value={buyRule} onChange={setBuyRule} />

        <label className="flex items-center gap-2 text-sm text-ink cursor-pointer">
          <input type="checkbox" checked={hasSell} onChange={(e) => setHasSell(e.target.checked)} className="rounded border-paper-border" />
          This contract also covers selling energy back to the grid
        </label>
        {hasSell && <PricingRuleEditor label="Selling energy" value={sellRule} onChange={setSellRule} />}

        {error && <p className="text-xs text-red-500">{error}</p>}
        <Button onClick={handleSubmit} disabled={pending} className="w-full">
          {pending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Modal>
  )
}
